/** @prettier */

import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useLoaderData, useParams} from 'react-router-dom';
import {Grid, Card, CardContent, Box, Stack} from '@mui/material';

//MTH component import
import IUAddDialog from './IUAddDialog';
import IUEditDialog from './IUEditDialog';
import IUDeleteDialog from './IUDeleteDialog';
import CardContents from '../../component/CardContents';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import GWConfigUpdateDrawer from '../gw-registration/GWConfigUpdateDrawer';
//API import
import {
  getCompositionsGateway,
  getCompositionsRTUnitByUnitID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';
import {useOutlet} from '../gw-registration/GWMain';
import {gwInfoSort, sortByUnicode} from '../../functions/sort';

type LoaderReturnType = {
  /** サイトに登録されているゲートウェイの情報 */
  allGWInfo: apiIF.res.getCompositionsGateway;
  /** 表示しているゲートウェイが課金しているかどうか */
  isBilling: boolean;
  /** 表示しているゲートウェイに登録されている熱源機の情報 */
  rtUnit: apiIF.res.getCompositionsRTUnitByUnitID;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (
    params.siteID === undefined ||
    params.gwID === undefined ||
    params.ouID === undefined
  ) {
    return undefined;
  }

  try {
    const promises = [];
    //ゲートウェイの情報を取得
    promises.push(getCompositionsGateway(params.siteID, {'max-result': 100}));
    //熱源機の情報を取得
    promises.push(
      getCompositionsRTUnitByUnitID(params.siteID, params.gwID, params.ouID)
    );
    const response = await Promise.all(promises);

    const allGWInfo = response[0] as apiIF.res.getCompositionsGateway;
    const rtUnit = response[1] as apiIF.res.getCompositionsRTUnitByUnitID;

    //課金状況の判定
    let isBilling = false;
    const displayGWInfo = allGWInfo['gateway-informations'].find(gwInfo => {
      return gwInfo['gateway-id'] === params.gwID;
    });
    if (
      displayGWInfo !== undefined &&
      displayGWInfo['billing-status'] !== undefined
    ) {
      isBilling = displayGWInfo['billing-status'] !== 'unpaid';
    }

    //表示内容をUnicode順に並び替え
    rtUnit['indoor-unit-informations'].sort(sortByUnicode('indoor-unit-name'));
    return {
      allGWInfo: gwInfoSort(allGWInfo),
      isBilling: isBilling,
      rtUnit: rtUnit,
    };
  } catch {
    return undefined;
  }
}

/**
 * RT通信設定の室内機一覧
 * @returns JSX
 */
const IURegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータ取得
  const {siteID, gwID} = useParams();
  //親コンポーネントから値を取得
  const {setAllGWInfo} = useOutlet();

  const [isBilling, setIsBilling] = useState(false); //表示しているゲートウェイが課金しているかどうか
  //prettier-ignore
  const [ouInfo, setOUInfo] = useState<apiIF.res.getCompositionsRTUnitByUnitID | null>(null); //熱源機の情報
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedIUID, setSelectedIUID] = useState(''); //選択した室内機のID
  const [hasConfigGW, setHasConfigGW] = useState<boolean>(false); //GW毎の構成情報未反映の有無

  /** 室内機追加ボタンの名前 */
  const addButtonName = trans(
    'IURegistration.remoteTerminalUnit.buttonTxt.addUnit'
  );

  //熱源機の情報を取得
  useEffect(() => {
    if (loaderData !== undefined) {
      setAllGWInfo(loaderData.allGWInfo);
      setIsBilling(loaderData.isBilling);
      setOUInfo(loaderData.rtUnit);

      //構成情報未反映の有無をチェック
      for (const gwInfo of loaderData.allGWInfo['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        setHasConfigGW(
          loaderData.isBilling &&
            gwInfo['composition-receipt-version'] !== undefined &&
            gwInfo['composition-receipt-version'] !== 'latest'
        );
        break;
      }
    }
  }, [loaderData, gwID, setAllGWInfo]);

  //空調機の情報が更新された場合、ツリービューを更新
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    getCompositionsGateway(siteID, {'max-result': 100}).then(response => {
      response = gwInfoSort(response);
      setAllGWInfo(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouInfo]);

  /**
   * Editボタン押下時処理
   * @param iuID 室内機ID
   */
  const openEditDialog = (iuID: string) => {
    setIsOpenEditDialog(true);
    setSelectedIUID(iuID);
  };
  /**
   * Deleteボタン押下時処理
   * @param iuID 室内機ID
   */
  const openDeleteDialog = (iuID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedIUID(iuID);
  };

  /**
   * 歯車メニューリスト
   * @param iuID 室内機ID
   * @returns メニュー一覧
   */
  const gearMenuItem = (iuID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-iuEdit${iuID}`,
        label: trans('IURegistration.menuItem.edit'),
        onClick: () => openEditDialog(iuID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `IURegistration.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        id: `mv-iuDelete${iuID}`,
        label: trans('IURegistration.menuItem.delete'),
        onClick: () => openDeleteDialog(iuID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `IURegistration.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  //室外機の情報を取得できない場合
  if (ouInfo === null) {
    return <></>;
  }
  return (
    <>
      <Box className="gwSetting__title">
        {/* 室外機名称 */}
        {ouInfo['unit-name']}
      </Box>
      <Stack spacing={4}>
        {/* 室内機追加ボタン */}
        <Box className="gwSetting__contents--unit gwSetting__contents__button--add">
          <IUAddDialog
            addButtonName={addButtonName}
            setOUInfo={setOUInfo}
            ouInfo={ouInfo}
            isBilling={isBilling}
            setHasConfigGW={setHasConfigGW}
          />
        </Box>
        {/* 登録済室内機が無い場合、追加ボタンのみ表示 */}
        {ouInfo['indoor-unit-informations'].length === 0 ? (
          <Card className="gwSetting__contents--unit">
            <CardContent className="gwSetting__contents__noRegister">
              {trans('IURegistration.remoteTerminalUnit.instruction', {
                name: addButtonName,
              })}
            </CardContent>
          </Card>
        ) : (
          //登録済室内機
          ouInfo['indoor-unit-informations'].map(iuInfo => (
            <Card
              id={`mv-boxIu${iuInfo['unit-id']}`}
              key={iuInfo['unit-id']}
              className="gwSetting__contents--unit">
              <CardContent>
                <Stack spacing={2}>
                  {/* 室内機名称と歯車 */}
                  <Grid
                    container
                    justifyContent="space-between"
                    className="gwSetting__contents__title">
                    {/* 室内機名称 */}
                    <Grid item xs="auto">
                      {iuInfo['indoor-unit-name']}
                    </Grid>
                    {/* 歯車ボタン */}
                    <Grid item xs={1}>
                      <GearMenu
                        id={`mv-btnIUSettingOpen${iuInfo['unit-id']}`}
                        menuItem={gearMenuItem(iuInfo['unit-id'])}
                      />
                      {/* 選択した室内機のダイアログのみ出す */}
                      {iuInfo['unit-id'] === selectedIUID ? (
                        <>
                          <IUEditDialog
                            iuID={iuInfo['unit-id']}
                            ouInfo={ouInfo}
                            setOUInfo={setOUInfo}
                            isOpenDialog={isOpenEditDialog}
                            setIsOpenDialog={setIsOpenEditDialog}
                            isBilling={isBilling}
                            setHasConfigGW={setHasConfigGW}
                          />
                          <IUDeleteDialog
                            iuID={iuInfo['unit-id']}
                            iuName={iuInfo['indoor-unit-name']}
                            ouInfo={ouInfo}
                            setOUInfo={setOUInfo}
                            isOpenDialog={isOpenDeleteDialog}
                            setIsOpenDialog={setIsOpenDeleteDialog}
                            isBilling={isBilling}
                            setHasConfigGW={setHasConfigGW}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                  <Stack spacing={2} className="gwSetting__contents__contents">
                    {/** アドレス */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'IURegistration.remoteTerminalUnit.item.title.unitAddress'
                      )}
                      value={iuInfo.address}
                    />
                    {/** 庫内温度異常とするための閾値 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'IURegistration.remoteTerminalUnit.item.title.errThresholds'
                      )}
                      value={`${trans(
                        'IURegistration.remoteTerminalUnit.item.value.errThresholds',
                        {value: iuInfo['error-threshold']}
                      )}`}
                    />
                    {/** 庫内温度と設定温度の温度差の警戒レベルを判定するための境界（低い側） */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'IURegistration.remoteTerminalUnit.item.title.warningThresholdsLow'
                      )}
                      value={`${
                        iuInfo['temperature-boundary']['low-boundary']
                      }${trans(
                        'IURegistration.remoteTerminalUnit.item.value.unit'
                      )}`}
                    />
                    {/** 庫内温度と設定温度の温度差の警戒レベルを判定するための境界（高い側） */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'IURegistration.remoteTerminalUnit.item.title.warningThresholdsHigh'
                      )}
                      value={`${
                        iuInfo['temperature-boundary']['high-boundary']
                      }${trans(
                        'IURegistration.remoteTerminalUnit.item.value.unit'
                      )}`}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
      {/* 構成情報反映ボタン */}
      <GWConfigUpdateDrawer
        hasConfigGW={hasConfigGW}
        setHasConfigGW={setHasConfigGW}
        hasUnit={true}
      />
    </>
  );
};
export default IURegistration;
